import * as SliderPrimitive from '@radix-ui/react-slider';
import React, { useMemo, useState } from 'react';

import { cn } from '@/lib/utils';

import { formatLeverage } from '../utils';

// Always brand lime (per Alex 2026-07-11): the slider is configuration, and
// the risk story is carried by the always-visible liq numbers — a red slider
// on a 3× market read as alarming, not informative.
const STYLE = {
    range: 'bg-primary',
    thumb: 'border-primary ring-primary/60',
    chip: 'border-primary/40 text-primary',
};

interface LeverageSliderProps {
    /** Every leverage the backend accepts for this pair, ascending.
     *  Empty = market metadata still loading (renders a skeleton). */
    scale: number[];
    /** Committed leverage (store value). */
    value: number;
    /** Called once, on release/keyup — not per drag frame. */
    onCommit: (value: number) => void;
    disabled?: boolean;
    /**
     * Render the value chip as a number input so leverage can be typed
     * directly — commits on blur/Enter, snapped to the nearest whole-number
     * ladder option. Omitted = read-only chip.
     */
    editable?: boolean;
}

/**
 * Slim leverage slider that snaps to the pair's real leverage ladder.
 * Index-based so it works for both explicit leverageList markets and
 * min/max/step markets; drag state is local and only the released value
 * commits to the store (one quote refetch per adjustment, not forty).
 */
export const LeverageSlider: React.FC<LeverageSliderProps> = ({
    scale,
    value,
    onCommit,
    disabled,
    editable,
}) => {
    const committedIndex = useMemo(() => {
        if (scale.length === 0) return 0;
        let best = 0;
        for (let i = 1; i < scale.length; i += 1) {
            if (Math.abs(scale[i] - value) < Math.abs(scale[best] - value)) {
                best = i;
            }
        }
        return best;
    }, [scale, value]);

    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const index = dragIndex ?? committedIndex;
    const current = scale[index] ?? value;

    // Typed-input draft (editable mode only); null = not editing.
    const [draft, setDraft] = useState<string | null>(null);
    const commitDraft = () => {
        if (draft === null) return;
        const parsed = Number.parseFloat(draft);
        setDraft(null);
        if (!Number.isFinite(parsed) || scale.length === 0) return;
        let nearest = scale[0];
        for (const option of scale) {
            if (Math.abs(option - parsed) < Math.abs(nearest - parsed)) {
                nearest = option;
            }
        }
        if (nearest !== value) onCommit(nearest);
    };

    // Metadata not loaded yet — skeleton, never a fake ladder.
    if (scale.length === 0) {
        return (
            <div className="flex h-6 items-center gap-2.5">
                <div className="h-1 grow animate-pulse rounded-full bg-white/[0.07] inset-ring inset-ring-white/35" />
                <span className="w-11 shrink-0 rounded-md border border-border bg-background/40 px-1.5 py-0.5 text-center font-mono text-[11px] text-muted-foreground">
                    —
                </span>
            </div>
        );
    }

    return (
        <div className="flex items-center gap-2.5">
            <span className="shrink-0 font-mono text-[9px] tabular-nums text-muted-foreground">
                {formatLeverage(scale[0])}
            </span>

            <SliderPrimitive.Root
                value={[index]}
                min={0}
                max={scale.length - 1}
                step={1}
                disabled={disabled}
                onValueChange={([i]) => setDragIndex(i ?? null)}
                onValueCommit={([i]) => {
                    setDragIndex(null);
                    const next = scale[i ?? committedIndex];
                    if (next !== undefined && next !== value) onCommit(next);
                }}
                // h-6 gives a fat touch target around the slim 4px track.
                className="relative flex h-6 grow touch-none select-none items-center data-disabled:opacity-50"
            >
                <SliderPrimitive.Track className="relative h-1 grow overflow-hidden rounded-full bg-white/[0.07] inset-ring inset-ring-white/35">
                    <SliderPrimitive.Range
                        className={cn(
                            'absolute h-full rounded-full transition-colors',
                            STYLE.range,
                        )}
                    />
                </SliderPrimitive.Track>
                <SliderPrimitive.Thumb
                    aria-label="Leverage"
                    // The slider walks ladder indexes — announce the actual
                    // multiplier, not "3 of 19".
                    aria-valuetext={formatLeverage(current)}
                    className={cn(
                        'block size-3.5 cursor-grab rounded-full border-2 bg-background shadow-[0_1px_4px_rgba(0,0,0,0.6)] transition-[color,box-shadow] hover:ring-4 focus-visible:outline-none focus-visible:ring-4 active:cursor-grabbing',
                        STYLE.thumb,
                    )}
                />
            </SliderPrimitive.Root>

            <span className="shrink-0 font-mono text-[9px] tabular-nums text-muted-foreground">
                {formatLeverage(scale[scale.length - 1])}
            </span>

            {/* Live value chip — updates during the drag. In editable mode
                it's a real input: type a multiplier, commits on blur/Enter,
                snapped to the pair's ladder. */}
            {editable ? (
                <input
                    type="number"
                    // Whole multipliers only — a typed 1.5 or 19.9 still
                    // snaps to the nearest rung on commit.
                    inputMode="numeric"
                    min={scale[0]}
                    max={scale[scale.length - 1]}
                    step={1}
                    aria-label="Leverage multiplier"
                    disabled={disabled}
                    value={draft ?? String(current)}
                    onFocus={(e) => {
                        setDraft(String(current));
                        e.target.select();
                    }}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={commitDraft}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') e.currentTarget.blur();
                        if (e.key === 'Escape') {
                            setDraft(null);
                            e.currentTarget.blur();
                        }
                    }}
                    className={cn(
                        'w-12 shrink-0 rounded-md border bg-background/40 px-1.5 py-0.5 text-center font-mono text-[11px] font-bold tabular-nums transition-colors focus:outline-none focus:ring-2 focus:ring-ring/40 disabled:opacity-50',
                        STYLE.chip,
                    )}
                />
            ) : (
                <span
                    className={cn(
                        'w-11 shrink-0 rounded-md border bg-background/40 px-1.5 py-0.5 text-center font-mono text-[11px] font-bold tabular-nums transition-colors',
                        STYLE.chip,
                    )}
                >
                    {formatLeverage(current)}
                </span>
            )}
        </div>
    );
};
